import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AppComponent } from 'src/app/app.component';
import { NodeConfigurationDTO } from 'src/app/model/configuration/node-configuration-dto';
import { SnmpAuthentication } from 'src/app/model/configuration/snmp/snmp-authentication';
import { SnmpDeviceConfigurationDTO } from 'src/app/model/configuration/snmp/snmp-device-configuration-dto';
import { SnmpObjectConfigurationDTO } from 'src/app/model/configuration/snmp/snmp-object-configuration-dto';
import { SnmpSecurityLevel } from 'src/app/model/configuration/snmp/snmp-security-level';
import { SnmpVersionCode } from 'src/app/model/configuration/snmp/snmp-version-code';
import { LoadIndicatorService } from 'src/app/services/load-indicator.service';
import { NodeService } from 'src/app/services/node.service';
import { SnmpService } from 'src/app/services/snmp.service';
import { MibDTO } from 'src/app/model/configuration/snmp/mib-dto';
import { MibObjectSelectorData, MibObjectSelectorDialogComponent } from '../../mib-object-selector-dialog/mib-object-selector-dialog.component';
import { DataSourceVariableConfigurationDTO } from 'src/app/model/configuration/datasources/data-source-variable-configuration-dto';
import { DataSourceFeatureConfigurationDTO } from 'src/app/model/configuration/datasources/data-source-feature-configuration-dto';
import { DataSourceConfigurationDTO } from 'src/app/model/configuration/datasources/data-source-configuration-dto';
import cloneDeep from 'lodash.clonedeep';
import { CoreStatus } from 'src/app/model/nodes/status/core-status';

@Component({
    selector: 'app-node-configuration-main',
    templateUrl: './node-configuration-main.component.html',
    styleUrl: './node-configuration-main.component.scss'
})
export class NodeConfigurationMainComponent implements OnInit {
    public readonly tabRoot: string = 'root'
    public readonly tabBackend: string = 'backend'
    public readonly tabParser: string = 'parser'
    public readonly tabSnmp: string = 'snmp'
    public readonly tabDetector: string = 'detector'
    public readonly tabDiagnoser: string = 'diagnoser'
    public readonly tabActuator: string = 'actuator'
    public readonly tabDataSources: string = 'datasources'
    public working = false
    public readMode = true;
    @ViewChildren("mibFileSelector") fileInputs?: QueryList<ElementRef>;

    public currentTab: string = this.tabRoot
    public id: number = -1

    public versions = Object.keys(SnmpVersionCode).filter(key => isNaN(Number(key)))
    public securityLevels = Object.keys(SnmpSecurityLevel).filter(key => isNaN(Number(key)))
    public authentications = Object.keys(SnmpAuthentication).filter(key => isNaN(Number(key)))

    public configuration?: NodeConfigurationDTO
    public selectedDevice?: SnmpDeviceConfigurationDTO
    public selectedObject?: SnmpObjectConfigurationDTO

    /** Parser */
    public readonly parserMainSubtab: string = 'parsermain'
    public readonly parserSplitSubtab: string = 'parsersplit'
    public parserCurrentSubtab: string = this.parserMainSubtab

    /** Detector */
    public readonly detectorGeneralSubtab: string = 'detectorgeneral'
    public readonly detectorStaticCalibrationTab: string = 'staticcalibration'
    public readonly detectorDynamicCalibrationTab: string = 'dynamiccalibration'
    public detectorCurrentSubtab: string = this.detectorGeneralSubtab

    /** Diagnoser */
    public readonly diagnoserDenialOfServiceSubtab: string = 'diagnoserdos'
    public readonly diagnoserPortScanningSubTab: string = 'diagnoserport'
    public readonly diagnoserDataExfiltrationSubTab: string = 'diagnoserdataexfiltration'
    public readonly diagnoserSshSubTab: string = 'diagnoserssh'
    public diagnoserCurrentSubtab: string = this.diagnoserDenialOfServiceSubtab
    public inputWhitelistPorts: string = ''

    /** Actuator */
    public readonly actuatorHoneyPotSubtab: string = 'actuatorhoneypot'
    public actuatorCurrentSubtab: string = this.actuatorHoneyPotSubtab

    /* Data sources */
    public selectedDataSource?: DataSourceConfigurationDTO
    public selectedVariable?: DataSourceVariableConfigurationDTO
    public selectedFeature?: DataSourceFeatureConfigurationDTO
    public variableMatchTypes: string[] = ["String", "Number", "Time", "Timedelta", "Ip", "Duration", "Counter", "Regexp"]
    public featureMatchTypes: string[] = ["Single", "Default", "Multiple", "Range", "Regexp", "Total"]

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        public nodeService: NodeService,
        public snmpService: SnmpService,
        public loadIndicatorService: LoadIndicatorService,
        public dialog: MatDialog,
        public fb: FormBuilder,
        private readonly toastr: ToastrService
    ) {
        this.id = isNaN(+(this.route.snapshot.paramMap.get('id') ?? -1)) ? -1 : +(this.route.snapshot.paramMap.get('id') ?? -1)
        const viewText = this.id > -1 ? 'configuration: ' + this.id : 'configuration'
        AppComponent.updateAppHeader(viewText, 'node manager', '/node-manager')
    }

    ngOnInit(): void {
        this.loadNodeConfiguration()
    }

    returnToNodeManager(): void {
        this.router.navigate(['/node-manager'])
    }

    validSnmpDevice(dev: SnmpDeviceConfigurationDTO): boolean {
        let deviceIsCorrect: boolean = true

        if (!dev.name || !dev.version) {
            deviceIsCorrect = false
        }

        dev.objects.forEach((obj, index) => {
            if (!this.validSnmpObject(obj)) {
                deviceIsCorrect = false
            }

            obj.duplicatedOid = false

            dev.objects.forEach((obj2, index2) => {
                if (index !== index2 && obj.oid === obj2.oid) {
                    obj.duplicatedOid = true
                    obj2.duplicatedOid = true
                    deviceIsCorrect = false
                }
            })
        })

        return deviceIsCorrect
    }

    deviceWithDuplicateOid(device: SnmpDeviceConfigurationDTO): boolean {
        return device.objects.filter(x => x.duplicatedOid === true).length > 0
    }

    openLoadMib(): void {
        this.fileInputs?.first?.nativeElement.click()
    }

    public mibFileSelected(event: any): void {
        let file: File = event.target?.files[0]

        if (file.name.split(".").pop() !== 'mib') {
            this.toastr.error('El archivo subido debe tener la extensión .mib')
            return
        }

        this.loadIndicatorService.show()

        const formData = new FormData();
        formData.append("file", file);

        this.snmpService.uploadMibFile(formData)
            .then((dto: MibDTO) => {

                const data: MibObjectSelectorData = {
                    file: file.name,
                    configuredObjects: this.selectedDevice?.objects ?? [],
                    mib: dto
                }
                const dialogRef = this.dialog.open(MibObjectSelectorDialogComponent, {
                    data: data,
                });

                dialogRef.afterClosed().subscribe((selectedNewObjects: SnmpObjectConfigurationDTO[]) => {
                    this.fileInputs!.first.nativeElement.value = null
                    this.selectedDevice?.objects.unshift(...selectedNewObjects ?? [])
                })
            }).catch(e => {
                this.toastr.error('Error loading MIB file.')
            })
            .finally(() => {
                this.loadIndicatorService.hide()
            })
    }


    validSnmpObject(obj: SnmpObjectConfigurationDTO): boolean {
        return !!obj.name && !!obj.oid
    }

    validSnmpConfiguration(): boolean {
        const config = this.configuration?.snmp
        if (!config) return false

        let devicesAreCorrect = true
        config.devices.forEach((dev) => {
            if (!this.validSnmpDevice(dev)) {
                devicesAreCorrect = false
            }
        })

        return !!config.frequencyMinutes && !!config.timeoutSeconds && !!config.records && devicesAreCorrect
    }

    validRootConfiguration(): boolean {
        const config = this.configuration

        if (!config) return false
        return config.docker !== undefined && config.commandTimeout !== undefined && !!config.dataFolder
    }

    validBackendConfiguration(): boolean {
        const config = this.configuration?.backend

        if (!config) return false
        return !!config?.address && !!config?.port
    }

    validParserConfiguration(): boolean {
        const config = this.configuration?.parser

        return config?.deparsingThreshold !== undefined &&
            !!config.cores &&
            !!config.chunkSize &&
            !!config.learningPercentage &&
            !!config.endLearningPercentage
    }

    validDataSourcesConfiguration(): boolean {
        const dataSources = this.configuration?.dataSources
        let valid = true

        dataSources?.forEach(ds => {
            if (!this.validDataSource(ds)) {
                valid = false
            }
        })

        return valid
    }

    validDataSource(ds: DataSourceConfigurationDTO): boolean {
        const dsConf = ds.configuration
        dsConf.separator ??= ',';
        dsConf.nfcapd ??= false;

        const validParameters = !!ds.name &&
            dsConf?.nfcapd !== undefined &&
            !!dsConf.separator &&
            dsConf.structured !== undefined &&
            !!dsConf.timeArgument &&
            !!dsConf.timestampFormat

        let validVariables = true
        dsConf?.variables.forEach(dsv => {
            if (!this.validDataSourceVariable(dsv, ds)) {
                validVariables = false
            }
        })

        let validFeatures = true
        dsConf?.features.forEach(dsf => {
            if (!this.validDataSourceFeature(dsf)) {
                validFeatures = false
            }
        })

        return validParameters && validVariables && validFeatures
    }

    getSelectedDataSourceVariableNames(): string[] {
        if (!this.selectedDataSource) return []

        return this.selectedDataSource.configuration.variables.map(x => x.name)
    }

    validDataSourceVariable(dsv: DataSourceVariableConfigurationDTO, ds: DataSourceConfigurationDTO): boolean {
        return !!dsv.name &&
            !!dsv.matchType &&
            this.validDataSourcePosition(dsv, ds) &&
            this.validDataSourceRegexp(dsv, ds)
    }

    validDataSourcePosition(dsv: DataSourceVariableConfigurationDTO, ds: DataSourceConfigurationDTO): boolean {
        return (!!ds?.configuration.structured && dsv?.position !== undefined && dsv.position > -1) ||
            (!ds?.configuration.structured)
    }

    validDataSourceRegexp(dsv: DataSourceVariableConfigurationDTO, ds: DataSourceConfigurationDTO): boolean {
        return (!ds?.configuration.structured && !!dsv?.regexp) ||
            (!!ds?.configuration.structured)
    }

    validDataSourceFeature(dsf: DataSourceFeatureConfigurationDTO): boolean {
        return !!dsf.name &&
            !!dsf.variable &&
            !!dsf.matchType &&
            this.validDataSourceFeatureMatchType(dsf)
    }

    validDataSourceFeatureVariable(dsf: DataSourceFeatureConfigurationDTO, ds: DataSourceConfigurationDTO): boolean {
        return !!dsf.variable && ds.configuration.variables.map(x => x.name).includes(dsf.variable)
    }

    validDataSourceFeatureMatchType(dsf: DataSourceFeatureConfigurationDTO): boolean {
        return this.featureMatchTypeNeedsValue(dsf) && !!dsf.value ||
            this.featureMatchTypeNeedsValues(dsf) && !!dsf.values || (!this.featureMatchTypeNeedsValue(dsf) && !this.featureMatchTypeNeedsValues(dsf))
    }

    validDetectorGeneralConfiguration(): boolean {
        const configDetector = this.configuration?.detector

        return !!configDetector?.lv &&
            !!configDetector?.prep &&
            !!configDetector?.phase &&
            !!configDetector?.missingDataSelectedMethod
    }
    validDetectorConfiguration(): boolean {
        return this.validDetectorGeneralConfiguration() &&
            this.validDetectorStaticConfiguration() &&
            this.validDetectorDynamicConfiguration()
    }

    validDetectorStaticConfiguration(): boolean {
        return true
    }

    validDetectorDynamicConfiguration(): boolean {
        return this.validDynamicObservationsField() && this.validDynamicLambdaField()
    }

    validDynamicObservationsField(): boolean {
        const configDetector = this.configuration?.detector

        if (!configDetector?.lv) return false

        return configDetector?.dynamicCalibration?.observations !== undefined &&
            configDetector.dynamicCalibration.observations >= (configDetector.lv + 2)
    }

    validDynamicLambdaField(): boolean {
        const configDetector = this.configuration?.detector.dynamicCalibration

        return configDetector?.lambda !== undefined && configDetector.lambda >= 0 && configDetector.lambda <= 1
    }

    validNodeConfiguration(): boolean {
        return this.validParserConfiguration() && this.validSnmpConfiguration() && this.validDetectorConfiguration()
            && this.validDiagnoserConfiguration()
    }

    validDiagnoserConfiguration(): boolean {
        return this.validDiagnoserDenialOfServiceConfiguration() &&
            this.validDiagnoserPortScanningConfiguration() &&
            this.validDiagnoserDataExfiltrationConfiguration() &&
            this.validDiagnoserSshConfiguration()
    }

    validDiagnoserDenialOfServiceConfiguration(): boolean {
        const configDiagnoser = this.configuration?.diagnoser;

        return !!configDiagnoser?.denialOfService.thresholdHighPacketsLowDuration.threshold
            && !!configDiagnoser?.denialOfService.thresholdHighPacketsLowDuration.packets
            && !!configDiagnoser?.denialOfService.thresholdHighPacketsLowDuration.duration
            && !!configDiagnoser?.denialOfService.thresholdConnectionsPerIP
            && !!configDiagnoser?.denialOfService.thresholdConnectionsDurationZero
            && !!configDiagnoser?.denialOfService.thresholdPacketsPerMinute
    }

    validDiagnoserPortScanningConfiguration(): boolean {
        const configDiagnoser = this.configuration?.diagnoser;

        return !!configDiagnoser?.portScanning.thresholdMaxPorts && !!configDiagnoser?.portScanning.thresholdConnectionsPerIp
    }

    validDiagnoserDataExfiltrationConfiguration(): boolean {
        const configDiagnoser = this.configuration?.diagnoser;

        return !!configDiagnoser?.dataExfiltration.whitelistPorts
            && !!configDiagnoser?.dataExfiltration.thresholdMaxPackets
    }

    validDiagnoserSshConfiguration(): boolean {
        const configDiagnoser = this.configuration?.diagnoser;

        return !!configDiagnoser?.ssh.threshold
            && !!configDiagnoser?.ssh.thresholdTime
    }

    deselectSnmp(): void {
        this.selectedDevice = undefined
        this.selectedObject = undefined
    }

    selectSnmpDevice(device: SnmpDeviceConfigurationDTO): void {
        this.deselectSnmp()
        this.selectedDevice = device
    }

    selectSnmpObject(object: SnmpObjectConfigurationDTO): void {
        this.selectedObject = undefined
        this.selectedObject = object
    }

    addSnmpDevice(): void {
        if (!this.configuration) return

        if (!this.configuration?.snmp?.devices) {
            this.configuration.snmp.devices = []
        }

        this.configuration.snmp.devices.unshift({
            name: 'new device',
            address: '',
            port: 0,
            version: '',
            objects: []
        })
    }

    addDataSourceVariable(ds: DataSourceConfigurationDTO): void {
        if (!!ds.configuration && !ds.configuration?.variables) {
            ds.configuration.variables = []
        }

        ds.configuration?.variables.unshift({
            name: 'new variable'
        })
    }

    addDataSourceFeature(ds: DataSourceConfigurationDTO): void {
        if (!!ds.configuration && !ds.configuration?.features) {
            ds.configuration.features = []
        }

        ds.configuration?.features.unshift({
            name: 'new feature'
        })
    }

    selectDataSource(ds: DataSourceConfigurationDTO): void {
        this.deselectDataSources()
        this.selectedDataSource = ds
    }

    selectDataSourceVariable(dsv: DataSourceVariableConfigurationDTO): void {
        this.selectedFeature = undefined
        this.selectedVariable = dsv
    }

    selectDataSourceFeature(dsf: DataSourceFeatureConfigurationDTO): void {
        this.selectedVariable = undefined
        this.selectedFeature = dsf
    }

    dataSourceSelected(): boolean {
        return !!this.selectedDataSource
    }

    variableSelected(): boolean {
        return !!this.selectedVariable
    }

    featureSelected(): boolean {
        return !!this.selectedFeature
    }

    featureMatchTypeNeedsValue(dsf: DataSourceFeatureConfigurationDTO): boolean {
        if (!dsf) return false

        return dsf.matchType === 'Single' || dsf.matchType === 'Regexp'
    }

    featureMatchTypeNeedsValues(dsf: DataSourceFeatureConfigurationDTO): boolean {
        if (!dsf) return false

        return dsf.matchType === 'Multiple' || dsf.matchType === 'Range'
    }

    updateWhitelistPorts() {
        this.configuration!.diagnoser.dataExfiltration.whitelistPorts = Array.from(new Set(
            this.inputWhitelistPorts.split(',')
                .map(p => p.trim())
                .filter(p => p !== '' && !isNaN(Number(p)))
                .map(p => Number(p))
        ));
    }

    getSelectedFeatureValues(): string[] {
        if (!this.featureSelected()) { return [] }

        let values = this.selectedFeature?.frontValues?.replace(/,+/g, ",").replace(/(,\s*$)|(^,*)/, "").split(',') ?? []
        values = values.map(x => x.trim())

        if (this.selectedFeature?.matchType === 'Range') {
            values = values.splice(0, 2) ?? []
        }

        this.selectedFeature!.values = cloneDeep(values)

        return values
    }

    deselectDataSources(): void {
        this.selectedDataSource = undefined
        this.selectedVariable = undefined
        this.selectedFeature = undefined
    }

    deleteDataSourceVariable(dataSourceIndex: number, variableIndex: number): void {
        if (!this.configuration) return

        this.configuration.dataSources[dataSourceIndex].configuration.variables.splice(variableIndex, 1)
        this.selectedFeature = undefined
    }

    deleteDataSourceFeature(dataSourceIndex: number, featureIndex: number): void {
        if (!this.configuration) return

        this.configuration.dataSources[dataSourceIndex].configuration.features.splice(featureIndex, 1)
    }

    addSnmpObject(device: SnmpDeviceConfigurationDTO): void {
        device.objects.unshift({ name: 'new object', oid: '' })
        this.selectSnmpObject(device.objects[0])
    }

    deleteSnmpDevice(deviceIndex: number): void {
        if (this.configuration?.snmp?.devices && this.configuration.snmp.devices.length > 0) {
            this.configuration?.snmp.devices.splice(deviceIndex, 1)
            this.deselectSnmp()
        }
    }

    deleteSnmpObject(deviceIndex: number, objectIndex: number): void {
        if (this.configuration?.snmp?.devices && this.configuration.snmp.devices.length > 0) {
            this.configuration.snmp.devices[deviceIndex].objects.splice(objectIndex, 1)
            this.selectedObject = undefined
        }
    }

    snmpItemSelected(): boolean {
        return this.currentTab === this.tabSnmp && (!!this.selectedObject || !!this.selectedDevice)
    }

    loadNodeConfiguration(): void {
        this.loadIndicatorService.show()
        this.nodeService.getNodeConfiguration(this.id)
            .then(dto => {
                this.configuration = dto

                if (!!this.configuration.detector && !this.configuration.detector?.staticCalibration) {
                    this.configuration.detector.staticCalibration = {}
                }
                if (!!this.configuration.detector && !this.configuration.detector?.dynamicCalibration) {
                    this.configuration.detector.dynamicCalibration = {}
                }
                this.configuration.actuator ??= {
                    honeyPot: {
                        ssh: {
                            address: '',
                            port: 0
                        }
                    }
                }

                this.inputWhitelistPorts = this.configuration.diagnoser.dataExfiltration.whitelistPorts.join(',')

                return this.nodeService.getStatus(this.id)
            })
            .then(coreStatusDto => {
                this.readMode = CoreStatus[coreStatusDto.status as unknown as keyof typeof CoreStatus] !== CoreStatus.Inactive
            })
            .catch(_ => {
                this.toastr.error('Node data is not valid or cannot establish a connection.')
                this.router.navigate(['/node-manager'])
            }).finally(() => {
                this.loadIndicatorService.hide()
            });
    }

    showSnmpTab(): boolean {
        return this.currentTab === this.tabSnmp
    }

    showRootTab(): boolean {
        return this.currentTab === this.tabRoot
    }

    showBackendTab(): boolean {
        return this.currentTab === this.tabBackend
    }

    showParserTab(): boolean {
        return this.currentTab === this.tabParser
    }

    showDetectorTab(): boolean {
        return this.currentTab === this.tabDetector
    }

    showDiagnoserTab(): boolean {
        return this.currentTab === this.tabDiagnoser
    }

    showActuatorTab(): boolean {
        return this.currentTab === this.tabActuator
    }

    showDataSourcesTab(): boolean {
        return this.currentTab === this.tabDataSources
    }

    save(): void {
        if (this.validNodeConfiguration()) {
            this.working = true

            if (!this.configuration?.actuator?.honeyPot?.ssh?.address && (!this.configuration?.actuator?.honeyPot?.ssh?.port || this.configuration?.actuator?.honeyPot?.ssh?.port == 0)) {
                delete this.configuration!.actuator;
            }

            this.nodeService.saveNodeConfiguration(this.id, this.configuration!)
                .then(() => {
                    this.toastr.success('Configuration updated succesfully!')
                }).catch(() => {
                    this.toastr.error('Error updating node configuration.')
                }).finally(() => {
                    this.working = false
                })
        }
    }
}
